import { Link } from "react-router-dom";
import { PUBLIC_ROUTES } from "../../routes";
import MarketingShell from "./MarketingShell";

const PILLARS = [
  {
    title: "The chores it takes",
    body: "Appointment letters answered, repeat prescriptions requested, the utility form filled, the week's photos sorted and sent to the family. The digital errands that pile up around care — done overnight, checked by you in the morning.",
  },
  {
    title: "Still local, still signed",
    body: "Night Shift runs on the same Local Mind, under the same rules. Documents never leave the house; the Egress Ledger itemises anything that must — an email sent, a form submitted — and nothing else goes anywhere.",
  },
  {
    title: "Hardware that earns its keep",
    body: "A care humanoid is a serious purchase. Night Shift is how it pays for itself twice: carer by day, the household's private assistant by night — instead of an idle machine on a charging dock.",
  },
] as const;

const NIGHT_LOG = [
  "02:14 — docked, charging, house quiet",
  "02:15 — GP appointment letter drafted for Thursday",
  "02:31 — pharmacy repeat request prepared, awaiting morning approval",
  "02:48 — week's photos sorted; album drafted for Liz",
  "03:02 — one email queued to leave the house — itemised in the Egress Ledger",
] as const;

export default function NightShift() {
  return (
    <MarketingShell>
      <section className="sg-hero">
        <div className="sg-hero-copy">
          <p className="sg-eyebrow">Service · Night Shift</p>
          <h1>Docked doesn&rsquo;t mean idle.</h1>
          <p className="sg-hero-text">
            The same local mind that runs the house by day runs the
            household&rsquo;s digital chores by night — on hardware
            you own, with nothing leaving home. Every task is queued
            for your approval, every byte that leaves is signed, and
            by breakfast the paperwork is simply&hellip; done.
          </p>
          <div className="sg-hero-actions">
            <Link to={PUBLIC_ROUTES.signup} className="sg-btn sg-btn-primary">
              Request a pilot
            </Link>
            <Link to={PUBLIC_ROUTES.productLocalMind} className="sg-btn sg-btn-secondary">
              See Local Mind
            </Link>
          </div>
        </div>
      </section>

      <section className="mk-story">
        <div className="mk-story-card">
          <p className="sg-section-kicker">A night, logged</p>
          <h2>While Margaret sleeps.</h2>
          <ul className="mk-story-bullets">
            {NIGHT_LOG.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p>
            Nothing here was streamed, uploaded, or seen by anyone.
            The drafts wait for Margaret&rsquo;s yes at breakfast; the
            one email that left the house is the one line in the
            ledger. The morning handover the family trusts is
            written on the night shift too.
          </p>
        </div>
      </section>

      <section className="sg-pillars">
        <div className="sg-section-heading compact">
          <p className="sg-section-kicker">What the service does</p>
          <h2>A second shift, without a second thought.</h2>
        </div>
        <div className="sg-pillars-grid">
          {PILLARS.map((item) => (
            <article className="sg-pillar" key={item.title}>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="sg-final-cta">
        <p className="sg-section-kicker">Rolling out with pilot partners</p>
        <h2>Your robot&rsquo;s day rate, without the night off.</h2>
        <p>
          Night Shift ships to pilot homes first, task by task, with
          the family or the care provider approving what
          the robot is allowed to handle. If there&rsquo;s a chore
          you&rsquo;d hand over tomorrow, tell us what it is — that
          list is the roadmap.
        </p>
        <div className="sg-hero-actions center">
          <Link to={PUBLIC_ROUTES.signup} className="sg-btn sg-btn-primary">
            Request a pilot
          </Link>
          <Link to={PUBLIC_ROUTES.compliance} className="sg-btn sg-btn-secondary">
            See Compliance
          </Link>
        </div>
      </section>
    </MarketingShell>
  );
}
